/**
 * Next Action Engine — Lightweight, Beta-Safe
 * ─────────────────────────────────────────────
 * Returns ONE "Next Best Action" for a job based on current state.
 *
 * Non-destructive: this is a thin priority-mapper on top of the existing
 * `getJobSuggestedActions`. It does NOT introduce automation, AI, or new
 * rule engines — it just enforces a deterministic priority order so users
 * always see the single most important next step.
 *
 * Priority (spec):
 *   1. Issues
 *   2. Materials
 *   3. Crew
 *   4. Scheduling
 *   5. QA
 *   6. Financial (invoice)
 *
 * Consumers render the result inside their existing "Action Required"
 * surface and execute it via the standard runAction system.
 */
import type { Job, Issue } from "./types";
import { getJobSuggestedActions, type SuggestedAction } from "./suggestedActions";

/**
 * ─────────────────────────────────────────────────────────────
 * Next Action Brain (Deterministic v1)
 * ─────────────────────────────────────────────────────────────
 * `getNextAction(job, issues)` is the single deterministic facade
 * for "what's the one next thing on this job?" used by Push Mode.
 *
 * Hybrid priority (per product decision):
 *   0. CRITICAL open issue (high severity)  → Resolve Issue
 *   1. Permit not approved                  → Submit / Track Permit
 *   2. Materials not ordered/missing        → Generate PO
 *   3. Crew not assigned                    → Assign Crew
 *   4. Not scheduled                        → Schedule Install
 *   5. Any open issue                       → Resolve Issue
 *   6. QA not complete                      → Start QA
 *   7. Closeout not complete                → Start Closeout
 *   8. Invoice not sent                     → Send Invoice
 *   9. else                                 → Done
 *
 * Rules are mutually exclusive — exactly one action returned.
 * Internally delegates to `getJobSuggestedActions` so all surfaces
 * stay aligned with the existing suggestion catalog.
 */
export type NextActionType =
  | "resolve_issue"
  | "submit_permit"
  | "generate_po"
  | "assign_crew"
  | "schedule_install"
  | "start_qa"
  | "start_closeout"
  | "send_invoice"
  | "done";

export interface NextAction {
  action: NextActionType;
  label: string;
  reason: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  /** Optional underlying SuggestedAction for consumers that want CTA/executionMode. */
  suggested?: SuggestedAction;
}

const PRIORITY_MAP: Record<SuggestedAction["priority"], NextAction["priority"]> = {
  high: "HIGH",
  medium: "MEDIUM",
  low: "LOW",
};

function actionTypeForSuggestion(s: SuggestedAction): NextActionType {
  switch (s.icon) {
    case "issue": return "resolve_issue";
    case "permit": return "submit_permit";
    case "materials": return "generate_po";
    case "crew":
    case "dispatch": return "assign_crew";
    case "schedule": return "schedule_install";
    case "qa": return "start_qa";
    case "closeout": return s.cta?.toLowerCase().includes("invoice") ? "send_invoice" : "start_closeout";
    default: return "done";
  }
}

/**
 * Single-action deterministic brain. Returns exactly one next action
 * (or `done`). Pure — safe inside useMemo. Recompute after every
 * successful mutation to enable Push Mode chaining.
 */
export function getNextAction(job: Job, openIssues: Issue[] = []): NextAction {
  // Hybrid rule: an Urgent open issue jumps to the front of the line.
  const criticalIssue = openIssues.find(
    (i) => i.severity === "Urgent" && i.status !== "Resolved" && i.status !== "Closed" && i.jobId === job.id,
  );
  if (criticalIssue) {
    return {
      action: "resolve_issue",
      label: "Resolve Critical Issue",
      reason: criticalIssue.description || "High-severity issue is blocking progress.",
      priority: "HIGH",
    };
  }

  const suggested = getJobNextAction(job, openIssues);
  if (!suggested) {
    return {
      action: "done",
      label: "On Track",
      reason: "No outstanding actions — job is progressing normally.",
      priority: "LOW",
    };
  }

  return {
    action: actionTypeForSuggestion(suggested),
    label: suggested.cta || suggested.label,
    reason: suggested.description,
    priority: PRIORITY_MAP[suggested.priority],
    suggested,
  };
}

/** Maps SuggestedAction.icon → priority bucket (lower = more urgent). */
const ICON_PRIORITY: Record<SuggestedAction["icon"], number> = {
  review: 0,     // Project Review gates everything — most urgent
  issue: 1,      // Issues
  materials: 2,  // Materials
  permit: 2,     // Permit blocks materials/scheduling — same bucket
  crew: 3,       // Crew
  dispatch: 3,   // Crew/dispatch — same bucket
  schedule: 4,   // Scheduling
  qa: 5,         // QA
  closeout: 6,   // Financial / closeout
};

/**
 * Returns the single highest-priority next action for a job, or null when
 * the job is fully on-track (no suggested actions, no unsent invoices).
 *
 * Pure function — safe to call inside useMemo.
 */
export function getJobNextAction(job: Job, openIssues: Issue[]): SuggestedAction | null {
  const actions = getJobSuggestedActions(job, openIssues);

  // Spec PART 7: enforce strict priority bucket order, then high > medium > low.
  const sorted = [...actions].sort((a, b) => {
    const bucketA = ICON_PRIORITY[a.icon] ?? 99;
    const bucketB = ICON_PRIORITY[b.icon] ?? 99;
    if (bucketA !== bucketB) return bucketA - bucketB;
    const p = { high: 0, medium: 1, low: 2 };
    return p[a.priority] - p[b.priority];
  });

  if (sorted.length > 0) return sorted[0];

  // Financial fallback: invoice not yet sent on a completed-but-unbilled job.
  if (
    (job.currentStage === "Completed" || job.qaSubmitted) &&
    job.closeoutStatus !== "Closed" &&
    !job.financials?.invoices?.some(inv => inv.status === "sent" || inv.status === "paid")
  ) {
    return {
      label: "Send Final Invoice",
      description: "Job is complete but no invoice has been sent. Bill the customer to recognize revenue.",
      priority: "high",
      icon: "closeout",
      cta: "Send Invoice",
      link: `/jobs/${job.id}`,
      executionMode: "modal",
    };
  }

  return null;
}
